import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { TargetedOffersContext } from 'contexts/TargetedOffersContext';
import { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { getBrandAlpha3Codes } from 'lib/config/index';
import colors from 'styles/colors';
import sizes from 'styles/sizes';
import { BrandCode } from 'types/Brand';
import { CALL_ORIGIN_TARGETED_OFFERS, TARGETED_OFFERS_SERVICE_ERROR_MESSAGES } from '../../../contexts/CruiseSearchContext/constants';

import { Typography, useTheme } from '@mui/material';
import { isLoyaltyId } from 'contexts/CruiseSearchContext/utils/targetedOffers';
import { useFeatureFlags } from '../../../hooks/LaunchDarkly/useFeatureFlags';
import Input from '../Components/Input';

const SignInWrapper = styled.div`
	background: ${colors.functional.paleGrey};
	border-radius: 5px;
	margin-top: 20px;
	padding: 20px;
	width: 100%;
	position: relative;
	@media (min-width: ${sizes.media.tabletPortrait}px) {
		display: flex;
		flex-wrap: wrap;
		padding: 30px 25px 60px;
	}
`;
export const ButtonSignIn = styled.button`
	background-color: ${({ palette }) => palette.primary.main};
	border-radius: 3px;
	color: ${colors.primary.ice};
	height: 50px;
	margin-top: 10px;
	padding: 13px 30px;
	width: 100%;

	transition:
		background-color 0.2s cubic-bezier(0, 0, 0, 0),
		transform 0.1s cubic-bezier(0, 0, 0, 0);

	@media (min-width: ${sizes.media.tabletPortrait}px) {
		width: auto;
		margin: 10px;
	}
	@media (hover: hover) {
		&:hover {
			background-color: ${colors.secondary.deepCoveLight};
		}
	}
	&:active {
		transform: scale(0.98);
		background-color: ${colors.secondary.deepCove};
	}

	:disabled {
		opacity: 0.4;
		cursor: unset;
		background-color: ${colors.secondary.deepCove};
		transform: unset;
	}
`;
const Title = styled(Typography)`
	color: ${({ palette }) => palette.secondary.main};
	&.title {
		margin-bottom: 20px;
		${({ theme }) =>
			({
				[BrandCode.RoyalCaribbean]: `
		font-family: ${theme.typography.fontFamilyBold};
		font-size: 20px;

	`,
				[BrandCode.Celebrity]: `
    font-family: ${theme.typography.fontFamilySemiBold};
    font-size: 18px; 
	`,
			})[theme.brandCode]}
	}
`;

const Title2 = styled(Typography)``;

const ForgotLink = styled.p`
	line-height: 17px;
	font-size: 14px;
	color: ${colors.functional.blueButton};
	margin-top: 15px;
	cursor: pointer;
	@media (min-width: ${sizes.media.tabletPortrait}px) {
		position: absolute;
		bottom: 35px;
	}
`;

function SignIn({ setActiveFilter, setShowFiltersModal }) {
	const {
		brand,
		globalization,
		errorTargetedOffers,
		targetedOffersLoaded,
		targetedOffersLoading,
		openModal,
		closeFilters,
		errorTargetedOfferMessage,
		isInvalidEmailOrCASId,
		setIsInvalidEmailOrCASId,
		loyaltyIDOrEmailLogin,
		updateLoyaltyIDOrEmailLogin,
	} = useContext(CruiseSearchContext);
	const { enableEmailOnTargetedOffers } = useFeatureFlags();
	const { removeTargetedOffers, fetchTargetedOffers, closeTargetedOffersPill } = useContext(TargetedOffersContext);

	const { filtersDictionary } = globalization?.dictionaries || {};
	const {
		labelOffersLogIn,
		inputOffersEnterCrownAndAnchorSocietyID,
		inputOffersLoyaltyOrEmailPlaceholder,
		inputMyOffersLoyaltyInputPlaceholder,
		actionMyOffersSignIn,
	} = filtersDictionary;

	const wrapperRef = useRef();

	const openCASModal = () => {
		closeFilters();
		setActiveFilter?.(null);
		setShowFiltersModal?.(false);
		openModal(true);
	};

	const brandAlpha3Codes = getBrandAlpha3Codes();

	const offersLabelsByBrands = {
		inputOffersEnterEmailOrSocietyID: filtersDictionary[`inputOffersEnterEmailOrCrownAndAnchorSocietyID_${brandAlpha3Codes[brand]}`],
		actionMyOffersForgotID: filtersDictionary[`actionMyOffersForgotCASID_${brandAlpha3Codes[brand]}`],
	};

	const [isInvalidLogin, setIsInvalidLogin] = useState(isInvalidEmailOrCASId);
	const { palette } = useTheme();

	useEffect(() => {
		setIsInvalidLogin(isInvalidEmailOrCASId);
	}, [isInvalidEmailOrCASId]);

	useEffect(() => {
		if (
			targetedOffersLoaded &&
			!targetedOffersLoading &&
			!Object.values(TARGETED_OFFERS_SERVICE_ERROR_MESSAGES).includes(errorTargetedOffers?.code)
		) {
			closeTargetedOffersPill();
		}
	}, [targetedOffersLoaded, targetedOffersLoading]);

	const signIn = () => {
		removeTargetedOffers();
		const isLoyalty = isLoyaltyId(loyaltyIDOrEmailLogin.trim());
		const inputData = {
			...{ ...(isLoyalty && { loyaltyID: loyaltyIDOrEmailLogin }) },
			...{
				...(!isLoyalty && enableEmailOnTargetedOffers
					? {
							email: loyaltyIDOrEmailLogin,
						}
					: { loyaltyID: loyaltyIDOrEmailLogin }),
			},
			origin: CALL_ORIGIN_TARGETED_OFFERS.FORM,
		};

		fetchTargetedOffers(inputData);
	};

	const setLoyaltyIDOrEmailLogin = (input) => {
		const inputFormatted = input.replace(/\s/g, '');
		updateLoyaltyIDOrEmailLogin(inputFormatted);
	};

	return (
		<>
			<Title palette={palette} variant="title5" className="title" component="div">
				{labelOffersLogIn}
			</Title>
			<SignInWrapper ref={wrapperRef} id="sign-wrapper">
				<Input
					placeholder={enableEmailOnTargetedOffers ? inputOffersLoyaltyOrEmailPlaceholder : inputMyOffersLoyaltyInputPlaceholder}
					label={enableEmailOnTargetedOffers ? offersLabelsByBrands.inputOffersEnterEmailOrSocietyID : inputOffersEnterCrownAndAnchorSocietyID}
					errorMessage={errorTargetedOfferMessage}
					isInvalid={isInvalidLogin}
					onChange={(event) => {
						setIsInvalidEmailOrCASId(false);
						setLoyaltyIDOrEmailLogin(event.target.value);
					}}
					onKeyDown={(event) => {
						if (event.key === 'Enter' || event.keyCode === 13) {
							signIn();
						}
					}}
					value={loyaltyIDOrEmailLogin || ''}
				>
					{loyaltyIDOrEmailLogin}
				</Input>
				<ButtonSignIn palette={palette} fullWidth onClick={signIn} disabled={targetedOffersLoading}>
					<Title2 variant="calendarSemiBold" component="span">
						{actionMyOffersSignIn}
					</Title2>
				</ButtonSignIn>
				<ForgotLink onClick={openCASModal}>{offersLabelsByBrands.actionMyOffersForgotID}</ForgotLink>
			</SignInWrapper>
		</>
	);
}
export default SignIn;
